import { useState, useEffect, useRef, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import DatePicker from "react-datepicker";
import { format, subDays } from "date-fns";
import axios from "axios";
import { 
  Calendar, 
  Loader2, 
  MapPin, 
  AlertTriangle,
  CloudLightning,
  Info,
  X,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import useSEO from "@/hooks/useSEO";

import "mapbox-gl/dist/mapbox-gl.css";
import "react-datepicker/dist/react-datepicker.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Minnesota bounds (tightened to exclude WI/MI border)
const MN_BOUNDS = [
  [-97.23, 43.49], // Southwest
  [-89.60, 49.38]  // Northeast (tightened from -89.48)
];

const MN_CENTER = [-93.26, 44.97];
const DEFAULT_ZOOM = 6.5;

// Hail size color scale
const HAIL_COLORS = {
  small: { color: "#22c55e", label: "< 1 inch (Pea to Marble)" },
  medium: { color: "#eab308", label: "1-2 inches (Quarter to Golf Ball)" },
  large: { color: "#ef4444", label: "> 2 inches (Tennis Ball+)" }
};

export default function HailMap() {
  useSEO();
  
  const mapContainer = useRef(null);
  const map = useRef(null);
  const popupRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(subDays(new Date(), 1));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hailData, setHailData] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [showLegend, setShowLegend] = useState(true);

  // Initialize map - runs once on mount
  useEffect(() => {
    if (map.current) return;
    
    if (!MAPBOX_TOKEN) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Mapbox token is missing!");
      }
      setError("Map configuration error. Please contact support.");
      return;
    }
    
    mapboxgl.accessToken = MAPBOX_TOKEN;
    
    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/dark-v11",
        center: MN_CENTER,
        zoom: DEFAULT_ZOOM,
        maxBounds: MN_BOUNDS,
        minZoom: 5,
        maxZoom: 12
      });

      map.current.addControl(new mapboxgl.NavigationControl(), "top-right");

      map.current.on("error", (e) => {
        if (process.env.NODE_ENV === 'development') {
          console.error("Mapbox error:", e);
        }
      });
    
      map.current.on("load", () => {
        setMapLoaded(true);
        
        // Ensure map resizes to fill container
        map.current.resize();
      
      // Add hail data source (empty initially)
      map.current.addSource("hail-data", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });
      
      // Add fill layer for polygons
      map.current.addLayer({
        id: "hail-polygons-fill",
        type: "fill",
        source: "hail-data",
        paint: {
          "fill-color": ["get", "color"],
          "fill-opacity": 0.6
        }
      });
      
      // Add outline layer
      map.current.addLayer({
        id: "hail-polygons-outline",
        type: "line",
        source: "hail-data",
        paint: {
          "line-color": ["get", "color"],
          "line-width": 3,
          "line-opacity": 1
        }
      });

      // Add center points source for markers
      map.current.addSource("hail-centers", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });

      // Add circle markers at center of each hail report for better visibility
      map.current.addLayer({
        id: "hail-center-markers",
        type: "circle",
        source: "hail-centers",
        paint: {
          "circle-radius": [
            "interpolate", ["linear"], ["zoom"],
            5, 8,
            8, 12,
            12, 20
          ],
          "circle-color": ["get", "color"],
          "circle-stroke-width": 3,
          "circle-stroke-color": "#ffffff",
          "circle-opacity": 0.9
        }
      });

      // Add labels for hail size
      map.current.addLayer({
        id: "hail-labels",
        type: "symbol",
        source: "hail-centers",
        layout: {
          "text-field": ["concat", ["get", "max_size_in"], "\""],
          "text-size": 11,
          "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          "text-anchor": "center"
        },
        paint: {
          "text-color": "#ffffff",
          "text-halo-color": "#000000",
          "text-halo-width": 1
        }
      });

      // Helper to create popup content
      const createPopupContent = (props) => {
        const severityColor = props.color || "#888";
        const severityLabel = props.severity === "large" ? "Severe" : 
                             props.severity === "medium" ? "Moderate" : "Minor";
        return `
          <div style="font-family: system-ui, sans-serif; min-width: 200px;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
              <div style="width: 12px; height: 12px; border-radius: 50%; background: ${severityColor};"></div>
              <strong style="font-size: 14px;">${severityLabel} Hail</strong>
            </div>
            <div style="font-size: 24px; font-weight: bold; color: ${severityColor}; margin-bottom: 4px;">
              ${props.max_size_in}"
            </div>
            <div style="color: #666; font-size: 12px; margin-bottom: 8px;">
              ${props.severity === "large" ? "Tennis ball or larger" : 
                props.severity === "medium" ? "Quarter to golf ball" : "Pea to marble size"}
            </div>
            <div style="border-top: 1px solid #eee; padding-top: 8px; font-size: 12px; color: #888;">
              <div style="margin-bottom: 4px;">
                <strong>Location:</strong> ${props.location || "Minnesota"}
              </div>
              <div style="margin-bottom: 4px;">
                <strong>Date:</strong> ${props.event_date || "Unknown"}
              </div>
              ${props.timestamp_iso ? `<div><strong>Time:</strong> ${new Date(props.timestamp_iso).toLocaleTimeString()}</div>` : ""}
            </div>
          </div>
        `;
      };
      
      // Add click handler for polygons with popup
      map.current.on("click", "hail-polygons-fill", (e) => {
        if (e.features.length > 0) {
          const feature = e.features[0];
          const coords = e.lngLat;
          
          // Remove existing popup
          if (popupRef.current) popupRef.current.remove();
          
          popupRef.current = new mapboxgl.Popup({ 
            closeButton: true,
            closeOnClick: false,
            maxWidth: "280px"
          })
            .setLngLat(coords)
            .setHTML(createPopupContent(feature.properties))
            .addTo(map.current);
            
          setSelectedEvent(feature.properties);
        }
      });

      // Add click handler for center markers with popup
      map.current.on("click", "hail-center-markers", (e) => {
        if (e.features.length > 0) {
          const feature = e.features[0];
          const coords = e.lngLat;
          
          // Remove existing popup
          if (popupRef.current) popupRef.current.remove();
          
          popupRef.current = new mapboxgl.Popup({ 
            closeButton: true,
            closeOnClick: false,
            maxWidth: "280px"
          })
            .setLngLat(coords)
            .setHTML(createPopupContent(feature.properties))
            .addTo(map.current);
            
          setSelectedEvent(feature.properties);
        }
      });
      
      // Change cursor on hover
      map.current.on("mouseenter", "hail-polygons-fill", () => {
        map.current.getCanvas().style.cursor = "pointer";
      });

      map.current.on("mouseenter", "hail-center-markers", () => {
        map.current.getCanvas().style.cursor = "pointer";
      });
      
      map.current.on("mouseleave", "hail-polygons-fill", () => {
        map.current.getCanvas().style.cursor = "";
      });

      map.current.on("mouseleave", "hail-center-markers", () => {
        map.current.getCanvas().style.cursor = "";
      });
    });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Failed to initialize map:", err);
      }
      setError("Failed to load map. Please refresh the page.");
    }

    return () => {
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Fetch hail data when date changes
  const fetchHailData = useCallback(async (date) => {
    if (!mapLoaded) return;
    
    setLoading(true);
    setError(null);
    setSelectedEvent(null);
    
    const dateStr = format(date, "yyyy-MM-dd");
    
    try {
      const response = await axios.get(`${API}/hail-data`, {
        params: { date: dateStr }
      });
      
      const geojson = response.data;
      setHailData(geojson);
      
      // Update map source with polygon data
      if (map.current && map.current.getSource("hail-data")) {
        map.current.getSource("hail-data").setData(geojson);
      }

      // Create center points for circle markers
      if (map.current && map.current.getSource("hail-centers")) {
        const centerPoints = {
          type: "FeatureCollection",
          features: geojson.features.map(feature => {
            // Calculate center of polygon
            const coords = feature.geometry.coordinates[0];
            const centerLon = coords.reduce((sum, c) => sum + c[0], 0) / coords.length;
            const centerLat = coords.reduce((sum, c) => sum + c[1], 0) / coords.length;
            
            return {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: [centerLon, centerLat]
              },
              properties: feature.properties
            };
          })
        };
        map.current.getSource("hail-centers").setData(centerPoints);
      }
      
      // Fit bounds to data if there are features
      if (geojson.features && geojson.features.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        geojson.features.forEach(feature => {
          if (feature.geometry && feature.geometry.coordinates) {
            feature.geometry.coordinates[0].forEach(coord => {
              bounds.extend(coord);
            });
          }
        });
        
        map.current.fitBounds(bounds, {
          padding: 100,
          maxZoom: 8
        });
      }
      
    } catch (err) {
      setError("Failed to fetch hail data. Please try again.");
      setHailData(null);
    } finally {
      setLoading(false);
    }
  }, [mapLoaded]);

  // Fetch data when map loads or date changes
  useEffect(() => {
    if (mapLoaded && selectedDate) {
      fetchHailData(selectedDate);
    }
  }, [mapLoaded, selectedDate, fetchHailData]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const goToPreviousDay = () => {
    setSelectedDate(prev => subDays(prev, 1));
  };

  const goToNextDay = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (selectedDate < tomorrow) {
      setSelectedDate(prev => {
        const next = new Date(prev);
        next.setDate(next.getDate() + 1);
        return next;
      });
    }
  };

  const eventCount = hailData?.features?.length || 0;
  const smallCount = hailData?.features?.filter(f => f.properties.severity === "small").length || 0;
  const mediumCount = hailData?.features?.filter(f => f.properties.severity === "medium").length || 0;
  const largeCount = hailData?.features?.filter(f => f.properties.severity === "large").length || 0;

  return (
    <div data-testid="hail-map-page" className="relative w-full overflow-hidden bg-navy" style={{ height: 'calc(100vh - 80px)', marginTop: '80px' }}>
      {/* Map Container */}
      <div 
        ref={mapContainer} 
        className="absolute inset-0" 
        style={{ width: '100%', height: '100%' }}
      />
      
      {/* Sidebar Toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="absolute top-4 left-4 z-20 bg-navy text-white p-2 rounded-lg shadow-lg hover:bg-navy-light transition-colors"
        aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Calendar className="w-5 h-5" />}
      </button>

      {/* Floating Sidebar */}
      <div 
        className={`absolute top-4 left-4 z-10 w-80 transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-[calc(100%+2rem)]"
        }`}
      >
        <div className="bg-navy/95 backdrop-blur-sm rounded-xl shadow-2xl overflow-hidden mt-12">
          {/* Header */}
          <div className="p-4 border-b border-navy-light">
            <div className="flex items-center gap-2 text-white mb-1">
              <CloudLightning className="w-5 h-5 text-orange" />
              <h2 className="font-heading font-semibold">Minnesota Hail Map</h2>
            </div>
            <p className="text-gray-400 text-sm">
              Historical hail storm data
            </p>
          </div>

          {/* Date Picker */}
          <div className="p-4 border-b border-navy-light">
            <label className="block text-gray-400 text-sm mb-2">Select Date</label>
            <div className="flex items-center gap-2">
              <button
                onClick={goToPreviousDay}
                className="p-2 bg-navy-light hover:bg-orange/20 rounded-lg transition-colors"
                aria-label="Previous day"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              
              <div className="flex-1">
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateChange}
                  maxDate={new Date()}
                  dateFormat="MMMM d, yyyy"
                  className="w-full bg-navy-light text-white px-4 py-2 rounded-lg border border-navy-border focus:border-orange focus:outline-none text-center"
                  calendarClassName="hail-map-calendar"
                />
              </div>
              
              <button
                onClick={goToNextDay}
                disabled={selectedDate >= new Date()}
                className="p-2 bg-navy-light hover:bg-orange/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Next day"
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="p-4 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-orange animate-spin mr-2" />
              <span className="text-gray-400">Loading hail data...</span>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="p-4 bg-red-500/10 border-t border-red-500/20">
              <div className="flex items-center gap-2 text-red-400">
                <AlertTriangle className="w-5 h-5" />
                <span className="text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Results Summary */}
          {!loading && hailData && (
            <div className="p-4 border-b border-navy-light">
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-400 text-sm">Reports Found</span>
                <span className="text-white font-semibold text-lg">{eventCount}</span>
              </div>
              
              {eventCount === 0 ? (
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Info className="w-4 h-4" />
                  <span>No hail reports for this date</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {smallCount > 0 && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: HAIL_COLORS.small.color }} />
                        <span className="text-gray-400 text-sm">&lt; 1 inch</span>
                      </div>
                      <span className="text-white text-sm">{smallCount}</span>
                    </div>
                  )}
                  {mediumCount > 0 && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: HAIL_COLORS.medium.color }} />
                        <span className="text-gray-400 text-sm">1-2 inches</span>
                      </div>
                      <span className="text-white text-sm">{mediumCount}</span>
                    </div>
                  )}
                  {largeCount > 0 && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: HAIL_COLORS.large.color }} />
                        <span className="text-gray-400 text-sm">&gt; 2 inches</span>
                      </div>
                      <span className="text-white text-sm">{largeCount}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Legend */}
          <div className="p-4">
            <h3 className="text-gray-400 text-sm mb-3">Hail Size Legend</h3>
            <div className="space-y-2">
              {Object.entries(HAIL_COLORS).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: value.color, opacity: 0.7 }}
                  />
                  <span className="text-gray-300 text-sm">{value.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Info */}
          <div className="p-4 bg-navy-light/50">
            <p className="text-gray-500 text-xs">
              Data provided by Xweather API. Click on any polygon to see details.
            </p>
          </div>
        </div>
      </div>

      {/* Floating Legend Toggle */}
      <div className="absolute bottom-20 right-4 z-20">
        <button
          onClick={() => setShowLegend(!showLegend)}
          className="bg-navy text-white p-3 rounded-lg shadow-lg hover:bg-navy-light transition-colors flex items-center gap-2"
          aria-label={showLegend ? "Hide legend" : "Show legend"}
        >
          {showLegend ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          <span className="text-sm">Legend</span>
        </button>
      </div>

      {/* Floating Legend Panel */}
      {showLegend && (
        <div className="absolute bottom-36 right-4 z-20 bg-navy/95 backdrop-blur-sm rounded-xl shadow-2xl border border-navy-border p-4 min-w-[220px]">
          <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
            <CloudLightning className="w-4 h-4 text-orange" />
            Hail Size Legend
          </h3>
          <div className="space-y-3">
            {Object.entries(HAIL_COLORS).map(([key, value]) => (
              <div key={key} className="flex items-center gap-3">
                <div 
                  className="w-5 h-5 rounded-full border-2 border-white/30"
                  style={{ backgroundColor: value.color }}
                />
                <span className="text-gray-300 text-sm">{value.label}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-navy-border">
            <p className="text-gray-500 text-xs">
              Click any marker for details
            </p>
          </div>
        </div>
      )}

      {/* Attribution */}
      <div className="absolute bottom-4 right-4 z-10 bg-black/50 px-2 py-1 rounded text-white text-xs">
        Powered by Xweather & Mapbox
      </div>
    </div>
  );
}
